import { useEffect, useRef, useState } from "react";
import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard, CalendarClock, MessageSquare, BellRing, BarChart3, Users, LogOut, Menu, X, Bell, BellOff,
  ExternalLink, Target, FileText,
  ShoppingBag, Package, Images, Star,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";
import apiClient from "./api";
import { BRAND } from "../data/mock";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/admin/bookings", label: "Bookings", icon: CalendarClock },
  { to: "/admin/enquiries", label: "Enquiries", icon: MessageSquare },
  { to: "/admin/reminders", label: "Reminders", icon: BellRing },
  { to: "/admin/tasks", label: "Tasks", icon: Target },
  { to: "/admin/orders", label: "Orders", icon: ShoppingBag, roles: ["admin", "staff"] },
  { to: "/admin/products", label: "Products", icon: Package, roles: ["admin", "staff"] },
  { to: "/admin/reviews", label: "Ratings", icon: Star, roles: ["admin", "staff"] },
  { to: "/admin/slides", label: "Homepage Slides", icon: Images, perm: "slides" },
  { to: "/admin/blog", label: "Blog", icon: FileText, adminOnly: true },
  { to: "/admin/reports", label: "Reports", icon: BarChart3, adminOnly: true },
  { to: "/admin/team", label: "Team", icon: Users, adminOnly: true },
];

const POLL_INTERVAL_MS = 30_000; // 30s — checks for new bookings/messages

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [notifEnabled, setNotifEnabled] = useState(false);
  const lastCountsRef = useRef(null);
  const [perms, setPerms] = useState({});
  useEffect(() => { apiClient.get("/api/admin/store/me/permissions").then((r) => setPerms(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (typeof Notification !== "undefined") {
      setNotifEnabled(Notification.permission === "granted");
    }
  }, []);

  // Poll for new bookings & enquiries & due reminders
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await apiClient.get("/api/admin/stats");
        if (cancelled) return;
        const next = {
          all_bookings: data.totals.all_bookings,
          new_messages: data.totals.new_messages,
          reminders_due: data.totals.reminders_due ?? 0,
        };
        if (lastCountsRef.current) {
          const diffBookings = next.all_bookings - lastCountsRef.current.all_bookings;
          const diffMessages = next.new_messages - lastCountsRef.current.new_messages;
          const diffDue = next.reminders_due - lastCountsRef.current.reminders_due;
          if (diffBookings > 0) {
            const msg = `🐾 ${diffBookings} new booking${diffBookings > 1 ? "s" : ""} received`;
            toast.success(msg);
            if (notifEnabled) new Notification("BarknFurs · New booking", { body: msg, icon: "/favicon.png" });
          }
          if (diffMessages > 0) {
            const msg = `📩 ${diffMessages} new enquir${diffMessages > 1 ? "ies" : "y"} received`;
            toast.success(msg);
            if (notifEnabled) new Notification("BarknFurs · New enquiry", { body: msg, icon: "/favicon.png" });
          }
          if (diffDue > 0) {
            const msg = `⏰ ${diffDue} reminder${diffDue > 1 ? "s" : ""} due — follow up now`;
            toast.warning(msg);
            if (notifEnabled) new Notification("BarknFurs · Reminder due", { body: msg, icon: "/favicon.png" });
          }
        }
        lastCountsRef.current = next;
      } catch {
        /* ignore */
      }
    };
    tick();
    const id = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [notifEnabled]);

  const enableNotifications = async () => {
    if (typeof Notification === "undefined") {
      toast.error("Notifications not supported in this browser");
      return;
    }
    if (Notification.permission === "granted") {
      setNotifEnabled(true);
      toast.success("Notifications already enabled");
      return;
    }
    const perm = await Notification.requestPermission();
    if (perm === "granted") {
      setNotifEnabled(true);
      new Notification("BarknFurs · Notifications on", {
        body: "You'll be alerted instantly about new bookings & enquiries.",
        icon: "/favicon.png",
      });
    } else {
      toast.error("Notifications blocked. Enable them in your browser settings.");
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 left-0 z-40 h-screen w-72 bg-[#0d0d0d] border-r border-white/10 flex flex-col transform transition-transform duration-300 ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="admin-sidebar"
      >
        <Link to="/admin" className="flex items-center gap-3 p-6 border-b border-white/10">
          <img src={BRAND.logoWhiteUrl} alt="BarknFurs" data-testid="admin-sidebar-logo" className="h-8 w-auto object-contain" />
          <div className="sr-only">
            <p className="font-heading font-bold text-base text-white leading-none">BarknFurs</p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9BCFF] mt-1">Admin Console</p>
          </div>
        </Link>

        <nav className="flex-1 p-4 space-y-1">
          {NAV.filter((n) => (!n.adminOnly || user?.role === "admin") && (!n.roles || n.roles.includes(user?.role)) && (!n.perm || perms[n.perm])).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.exact}
              data-testid={`admin-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[#FC5106] text-white"
                    : "text-white/70 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10 space-y-3">
          <button
            onClick={enableNotifications}
            data-testid="admin-toggle-notifications"
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-white/70 hover:bg-white/5 hover:text-white transition-colors"
          >
            {notifEnabled ? (
              <Bell className="w-4 h-4 text-[#C9BCFF]" />
            ) : (
              <BellOff className="w-4 h-4" />
            )}
            {notifEnabled ? "Notifications on" : "Enable notifications"}
          </button>
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-white/70 hover:bg-white/5 hover:text-white transition-colors"
            data-testid="admin-view-site"
          >
            <ExternalLink className="w-4 h-4" /> View website
          </a>
          <div className="px-4 py-3 rounded-xl bg-white/5 border border-white/10">
            <p className="text-xs text-white/40 uppercase tracking-wider">Signed in as</p>
            <p className="text-sm font-semibold text-white truncate">{user?.email}</p>
            <p className="text-[10px] uppercase tracking-wider text-[#FC5106] mt-0.5">{user?.role}</p>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-white/70 hover:bg-red-500/10 hover:text-red-300 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      </aside>

      {/* Backdrop on mobile */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-30 bg-black/60" onClick={() => setOpen(false)} />
      )}

      {/* Content */}
      <div className="flex-1 min-w-0">
        <header className="lg:hidden sticky top-0 z-20 bg-black/80 backdrop-blur-md border-b border-white/10 flex items-center justify-between px-4 py-3">
          <Link to="/admin" className="flex items-center gap-2">
            <img src={BRAND.logoWhiteUrl} alt="BarknFurs" data-testid="admin-mobile-logo" className="h-6 w-auto object-contain" />
            <p className="font-heading font-bold text-white sr-only">Admin</p>
          </Link>
          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="admin-mobile-toggle"
            className="p-2 rounded-full bg-white/5 border border-white/10 text-white"
            aria-label="Menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </header>
        <main className="p-4 sm:p-6 lg:p-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
